import { useEffect, useState } from "react";
import { VIEW_DURATION, MIN_VIEW_DURATION, THRESHOLD } from "../constants";

// Лента — бесконечный список, остальные типы рисуются поверх страницы без своего
// контейнера: следить за видимостью не у чего, перерисовка их только сломала бы.
const YANDEX_NO_REFRESH_TYPES = new Set(["feed", "fullscreen", "overlay", "floorAd", "inImage"]);

export function isYandexRefreshable(type) {
    return !YANDEX_NO_REFRESH_TYPES.has(type);
}

// Таймер без DOM и React: считает время в экране и зовёт onRefresh, когда блок
// провисел viewDuration. Скрытая вкладка ставит отсчёт на паузу.
export function createRefreshTimer({
    onRefresh,
    refreshOnLeave = false,
    viewDuration = VIEW_DURATION,
    minViewDuration = MIN_VIEW_DURATION,
    now = Date.now,
}) {
    let inView = false;
    let pageHidden = false;
    let timerId = null;
    let shownAt = null;

    const start = () => {
        if (timerId || !inView || pageHidden) {
            return;
        }

        shownAt = now();
        timerId = setTimeout(() => {
            timerId = null;
            shownAt = null;
            onRefresh();
            start();
        }, viewDuration);
    };

    const stop = () => {
        clearTimeout(timerId);
        timerId = null;

        const viewed = shownAt === null ? 0 : now() - shownAt;
        shownAt = null;

        return viewed;
    };

    return {
        setInView(value) {
            inView = value;

            if (value) {
                start();
                return;
            }

            const viewed = stop();
            // Блок перерисовывается уже вне экрана, чтобы при возврате к нему был новый показ.
            if (refreshOnLeave && viewed >= minViewDuration) {
                onRefresh();
            }
        },
        setPageHidden(value) {
            pageHidden = value;

            if (value) {
                stop();
            } else {
                start();
            }
        },
        dispose() {
            stop();
        },
    };
}

// Номер показа блока: 0 — первый, дальше растёт с каждым рефрешем. Компонент
// кладёт его в зависимости эффекта, который рисует рекламу.
export function useAdRefresh(ref, { enabled = true, refreshOnLeave = false, threshold = THRESHOLD } = {}) {
    const [round, setRound] = useState(0);

    useEffect(() => {
        const element = ref.current;

        if (!enabled || !element || typeof IntersectionObserver === "undefined") {
            return undefined;
        }

        const timer = createRefreshTimer({ onRefresh: () => setRound((value) => value + 1), refreshOnLeave });
        // isIntersecting истинен при любом пересечении, в том числе ниже порога.
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    timer.setInView(entry.isIntersecting && entry.intersectionRatio >= threshold);
                });
            },
            { threshold }
        );
        const onVisibilityChange = () => timer.setPageHidden(document.hidden);

        timer.setPageHidden(document.hidden);
        observer.observe(element);
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            observer.disconnect();
            document.removeEventListener("visibilitychange", onVisibilityChange);
            timer.dispose();
        };
    }, [enabled, refreshOnLeave, threshold]);

    return round;
}

// Повторный показ рисуется в новый вложенный контейнер: у каждого показа свой
// renderTo, а pageNumber отличает его от предыдущих показов того же блока.
export function renderYandex(options, container, round) {
    let renderOptions = options;

    if (round && container) {
        const target = document.createElement("div");
        target.id = `${container.id}-${round}`;
        container.innerHTML = "";
        container.appendChild(target);
        renderOptions = { ...options, renderTo: target.id, pageNumber: round + 1 };
    }

    window.yaContextCb = window.yaContextCb || [];
    window.yaContextCb.push(() => {
        window.Ya.Context.AdvManager.render(renderOptions);
    });
}
